import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { PersistedItem } from 'types/persistedStorage.ts'

/**
 * Parses a stored item, returns null if it is not a valid persisted item
 *
 * @param {string} stored
 * @returns {PersistedItem | null}
 */
const parsePersistedItem = (stored: string): PersistedItem | null => {
  try {
    const item: unknown = JSON.parse(stored)
    return !!item && typeof item === 'object' && 'value' in item && typeof item.value === 'string' ? (item as PersistedItem) : null
  } catch {
    return null
  }
}

/**
 * Gets a persisted item from localStorage, removes it when it is invalid or expired
 *
 * @param {string} key
 * @param {string} [legacyCookieName]
 * @returns {string | undefined}
 */
export const getPersistedItem = (key: string, legacyCookieName: string = key): string | undefined => {
  const stored = localStorage.getItem(key)

  // Cookies never persist in the iOS app (capacitor:// scheme), the fallback keeps the values web users still have in their cookies
  if (stored === null) return Cookies.get(legacyCookieName)

  const item = parsePersistedItem(stored)
  if (!item || (item.expiresAt && !dayjs().isBefore(item.expiresAt))) {
    localStorage.removeItem(key)
    return undefined
  }

  return item.value
}

/**
 * Persists an item in localStorage
 *
 * @param {string} key
 * @param {string} value
 * @param {number} [expiresInDays]
 */
export const setPersistedItem = (key: string, value: string, expiresInDays?: number): void => {
  const item: PersistedItem = {
    value,
    ...(typeof expiresInDays === 'number' && { expiresAt: dayjs().add(expiresInDays, 'day').toISOString() })
  }
  localStorage.setItem(key, JSON.stringify(item))
}
